import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Grid,
  GridItem,
  useColorModeValue,
  Icon,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { FaDollarSign } from 'react-icons/fa';
import { apiUsageManager, APIUsageData } from '../utils/apiUsageManager';
import { AIProvider } from './Settings';

interface APIUsageDisplayProps {
  currentProvider: AIProvider;
  onProviderChange?: (provider: AIProvider) => void;
}

const APIUsageDisplay: React.FC<APIUsageDisplayProps> = ({ currentProvider, onProviderChange }) => {
  const [selectedProvider, setSelectedProvider] = useState<AIProvider>(currentProvider);
  const [todayUsage, setTodayUsage] = useState<APIUsageData['usage']>({
    requests: 0,
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
  });
  const [monthUsage, setMonthUsage] = useState<APIUsageData['usage']>({
    requests: 0,
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
  });
  const [remainingQuota, setRemainingQuota] = useState<any>({});
  const [rateLimit, setRateLimit] = useState<any>({ allowed: true });

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const alertBg = useColorModeValue('orange.50', 'orange.900');

  useEffect(() => {
    setSelectedProvider(currentProvider);
  }, [currentProvider]);

  useEffect(() => {
    const updateUsage = () => {
      const today = apiUsageManager.getTodayUsage(selectedProvider);
      const month = apiUsageManager.getMonthUsage(selectedProvider);
      const remaining = apiUsageManager.getRemainingQuota(selectedProvider);
      const limit = apiUsageManager.checkRateLimit(selectedProvider);

      setTodayUsage(today);
      setMonthUsage(month);
      setRemainingQuota(remaining);
      setRateLimit(limit);
    };

    updateUsage();
    
    // 5秒ごとに更新
    const interval = setInterval(updateUsage, 5000);
    return () => clearInterval(interval);
  }, [selectedProvider]);

  const getProviderName = (provider: AIProvider) => {
    switch (provider) {
      case 'gemini-free': return 'Gemini (無料版)';
      case 'gemini': return 'Gemini Pro';
      case 'claude': return 'Claude';
      case 'gpt': return 'GPT';
      default: return provider;
    }
  };

  const getProviderColor = (provider: AIProvider) => {
    switch (provider) {
      case 'gemini-free': return 'blue';
      case 'gemini': return 'purple';
      case 'claude': return 'orange';
      case 'gpt': return 'green';
      default: return 'gray';
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString('ja-JP');
  };

  const formatCost = (cost?: number) => {
    if (!cost) return '$0.00';
    return `$${cost.toFixed(4)}`;
  };

  const quotaInfo = apiUsageManager.getQuotaInfo(selectedProvider);
  const isFreeTier = selectedProvider === 'gemini-free';

  const handleProviderChange = (provider: string) => {
    const newProvider = provider as AIProvider;
    setSelectedProvider(newProvider);
    if (onProviderChange) {
      onProviderChange(newProvider);
    }
  };

  return (
    <Box
      p={4}
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      shadow="sm"
    >
      <VStack spacing={4} align="stretch">
        <HStack justify="space-between">
          <Text fontSize="lg" fontWeight="bold">API使用状況</Text>
          <Select
            value={selectedProvider}
            onChange={(e) => handleProviderChange(e.target.value)}
            size="sm"
            width="200px"
          >
            <option value="gemini-free">Gemini (無料版)</option>
            <option value="gemini">Gemini Pro</option>
            <option value="claude">Claude</option>
            <option value="gpt">GPT</option>
          </Select>
        </HStack>

        {/* レート制限アラート */}
        {!rateLimit.allowed && (
          <Alert status="warning" borderRadius="md" bg={alertBg}>
            <AlertIcon />
            <Box>
              <AlertTitle>レート制限に達しました</AlertTitle>
              <AlertDescription>
                {rateLimit.reason}
                {rateLimit.resetIn && (
                  <Text fontSize="sm" mt={1}>
                    リセットまで: {Math.ceil(rateLimit.resetIn / 60)}分
                  </Text>
                )}
              </AlertDescription>
            </Box>
          </Alert>
        )}

        <Tabs size="sm" variant="enclosed">
          <TabList>
            <Tab>今日</Tab>
            <Tab>今月</Tab>
            <Tab>制限情報</Tab>
          </TabList>

          <TabPanels>
            {/* 今日の使用量 */}
            <TabPanel px={0} pt={4}>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <Stat>
                    <StatLabel>リクエスト数</StatLabel>
                    <StatNumber fontSize="2xl">{formatNumber(todayUsage.requests)}</StatNumber>
                    {quotaInfo?.limits.requestsPerDay && (
                      <StatHelpText>
                        / {formatNumber(quotaInfo.limits.requestsPerDay)} 回
                      </StatHelpText>
                    )}
                  </Stat>
                </GridItem>

                <GridItem>
                  <Stat>
                    <StatLabel>総トークン数</StatLabel>
                    <StatNumber fontSize="2xl">{formatNumber(todayUsage.totalTokens)}</StatNumber>
                    {quotaInfo?.limits.tokensPerDay && (
                      <StatHelpText>
                        / {formatNumber(quotaInfo.limits.tokensPerDay)}
                      </StatHelpText>
                    )}
                  </Stat>
                </GridItem>

                {!isFreeTier && (
                  <GridItem colSpan={2}>
                    <Stat>
                      <StatLabel>
                        <HStack spacing={1}>
                          <Icon as={FaDollarSign} />
                          <Text>推定コスト（今日）</Text>
                        </HStack>
                      </StatLabel>
                      <StatNumber fontSize="2xl" color="green.500">
                        {formatCost(todayUsage.estimatedCost)}
                      </StatNumber>
                      <StatHelpText>
                        入力: {formatNumber(todayUsage.inputTokens)} / 出力: {formatNumber(todayUsage.outputTokens)}
                      </StatHelpText>
                    </Stat>
                  </GridItem>
                )}
              </Grid>

              {/* 日次制限の進捗バー */}
              {quotaInfo?.limits.requestsPerDay && (
                <Box mt={4}>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="medium">日次リクエスト制限</Text>
                    <Text fontSize="sm" color="gray.500">
                      {formatNumber(remainingQuota.daily?.requests || 0)} 回 残り
                    </Text>
                  </HStack>
                  <Progress
                    value={(todayUsage.requests / quotaInfo.limits.requestsPerDay) * 100}
                    colorScheme={todayUsage.requests >= quotaInfo.limits.requestsPerDay * 0.8 ? 'red' : 'blue'}
                    size="sm"
                    borderRadius="md"
                  />
                </Box>
              )}
            </TabPanel>

            {/* 今月の使用量 */}
            <TabPanel px={0} pt={4}>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <Stat>
                    <StatLabel>総リクエスト数</StatLabel>
                    <StatNumber fontSize="2xl">{formatNumber(monthUsage.requests)}</StatNumber>
                  </Stat>
                </GridItem>

                <GridItem>
                  <Stat>
                    <StatLabel>総トークン数</StatLabel>
                    <StatNumber fontSize="2xl">{formatNumber(monthUsage.totalTokens)}</StatNumber>
                    {quotaInfo?.limits.tokensPerMonth && (
                      <StatHelpText>
                        / {formatNumber(quotaInfo.limits.tokensPerMonth)}
                      </StatHelpText>
                    )}
                  </Stat>
                </GridItem>

                {!isFreeTier && (
                  <GridItem colSpan={2}>
                    <Stat>
                      <StatLabel>
                        <HStack spacing={1}>
                          <Icon as={FaDollarSign} />
                          <Text>推定コスト（今月）</Text>
                        </HStack>
                      </StatLabel>
                      <StatNumber fontSize="2xl" color="green.500">
                        {formatCost(monthUsage.estimatedCost)}
                      </StatNumber>
                      <StatHelpText>
                        入力: {formatNumber(monthUsage.inputTokens)} / 出力: {formatNumber(monthUsage.outputTokens)}
                      </StatHelpText>
                    </Stat>
                  </GridItem>
                )}
              </Grid>

              {/* 月次制限の進捗バー */}
              {quotaInfo?.limits.tokensPerMonth && (
                <Box mt={4}>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="medium">月次トークン制限</Text>
                    <Text fontSize="sm" color="gray.500">
                      {formatNumber(remainingQuota.monthly?.tokens || 0)} トークン 残り
                    </Text>
                  </HStack>
                  <Progress
                    value={100 - (remainingQuota.percentage || 0)}
                    colorScheme={
                      remainingQuota.percentage <= 20 ? 'red' : 
                      remainingQuota.percentage <= 50 ? 'orange' : 
                      'green'
                    }
                    size="sm"
                    borderRadius="md"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {remainingQuota.percentage?.toFixed(1)}% 残り
                  </Text>
                </Box>
              )}
            </TabPanel>

            {/* 制限情報 */}
            <TabPanel px={0} pt={4}>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Badge colorScheme={getProviderColor(selectedProvider)} mb={2}>
                    {getProviderName(selectedProvider)}
                  </Badge>
                  
                  {quotaInfo && (
                    <VStack spacing={3} align="stretch">
                      {/* レート制限 */}
                      <Box>
                        <Text fontWeight="medium" mb={2}>レート制限</Text>
                        <VStack spacing={1} align="stretch" pl={4}>
                          {quotaInfo.limits.requestsPerMinute && (
                            <HStack justify="space-between">
                              <Text fontSize="sm">リクエスト/分:</Text>
                              <Text fontSize="sm" fontWeight="medium">
                                {formatNumber(quotaInfo.limits.requestsPerMinute)}
                              </Text>
                            </HStack>
                          )}
                          {quotaInfo.limits.requestsPerDay && (
                            <HStack justify="space-between">
                              <Text fontSize="sm">リクエスト/日:</Text>
                              <Text fontSize="sm" fontWeight="medium">
                                {formatNumber(quotaInfo.limits.requestsPerDay)}
                              </Text>
                            </HStack>
                          )}
                          {quotaInfo.limits.tokensPerMinute && (
                            <HStack justify="space-between">
                              <Text fontSize="sm">トークン/分:</Text>
                              <Text fontSize="sm" fontWeight="medium">
                                {formatNumber(quotaInfo.limits.tokensPerMinute)}
                              </Text>
                            </HStack>
                          )}
                          {quotaInfo.limits.tokensPerMonth && (
                            <HStack justify="space-between">
                              <Text fontSize="sm">トークン/月:</Text>
                              <Text fontSize="sm" fontWeight="medium">
                                {formatNumber(quotaInfo.limits.tokensPerMonth)}
                              </Text>
                            </HStack>
                          )}
                        </VStack>
                      </Box>

                      {/* 料金情報 */}
                      {quotaInfo.pricing && (
                        <>
                          <Divider />
                          <Box>
                            <Text fontWeight="medium" mb={2}>料金情報（USD）</Text>
                            <VStack spacing={1} align="stretch" pl={4}>
                              <HStack justify="space-between">
                                <Text fontSize="sm">入力トークン（1K）:</Text>
                                <Text fontSize="sm" fontWeight="medium" color="green.500">
                                  ${quotaInfo.pricing.inputTokensPer1K.toFixed(4)}
                                </Text>
                              </HStack>
                              <HStack justify="space-between">
                                <Text fontSize="sm">出力トークン（1K）:</Text>
                                <Text fontSize="sm" fontWeight="medium" color="green.500">
                                  ${quotaInfo.pricing.outputTokensPer1K.toFixed(4)}
                                </Text>
                              </HStack>
                            </VStack>
                          </Box>
                        </>
                      )}

                      {isFreeTier && (
                        <>
                          <Divider />
                          <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <Box>
                              <AlertTitle fontSize="sm">無料プラン</AlertTitle>
                              <AlertDescription fontSize="xs">
                                Gemini無料版をご利用中です。コストは発生しません。
                              </AlertDescription>
                            </Box>
                          </Alert>
                        </>
                      )}
                    </VStack>
                  )}
                </Box>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
};

export default APIUsageDisplay;